/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import { FC, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Avatar,
  Box,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Divider
} from '@material-ui/core';
// import { InputField } from './input-field';
import { channelPartnerApi } from 'src/api/newChannelPartner/channelPartner';
import { ResponseCodes } from 'src/enums/responseCodes';
import { technicianApi } from 'src/api/newTechnician/technician';
import { s3FileApi } from 'src/api/s3file';
import viewContext from 'src/viewContext';
import PushPinIcon from '@material-ui/icons/PushPin';
import { dealerApi } from 'src/api/dealer/dealer';
import { useAuth } from '../hooks/use-auth';
import { usePopover } from '../hooks/use-popover';
import { ChevronRight as ChevronRightIcon } from '../icons/chevron-right';
import { ChevronLeft as ChevronLeftIcon } from '../icons/chevron-left';
import { Logout as LogoutIcon } from '../icons/logout';
import { OfficeBuilding as OfficeBuildingIcon } from '../icons/office-building';
import { User as UserIcon } from '../icons/user';
import { Roles } from '../enums/role-based-auth-guard';
// import { lightNeutral } from '../colors';

interface AccountPopoverProps {
  darkMode: boolean;
  onSwitchTheme: () => void;
  showName?: boolean;
  onPin?: () => void;
  pinned?: boolean;
}

export const AccountPopover: FC<AccountPopoverProps> = (props) => {
  const {
    darkMode,
    onSwitchTheme,
    showName,
    onPin,
    pinned,
    ...other
  } = props;
  const navigate = useNavigate();
  const { logout, user } = useAuth(); // get user details from auth hook
  const [anchorRef, open, handleOpen, handleClose] = usePopover();
  const [userName, setUserName] = useState<string>('');
  const [organisationName, setOrganisationName] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string>('');
  const { tabView } = useContext(viewContext);

  const handleLogout = async (): Promise<void> => {
    // clear localstorage on logout
    localStorage.clear();
    try {
      handleClose();
      await logout();
      navigate('/');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  };

  const setImageData = async (imagePath: string) => {
    const url = await s3FileApi.getImageFile(imagePath);
    setImageUrl(url || '');
  };

  const getChannelPartnerDetails = async () => {
    setOrganisationName(user?.cpName);
    const result = await channelPartnerApi.getChannelPartnerProfile();
    if (result?.statusCode === ResponseCodes.OK) {
      if (result?.responseData?.managementDetails?.name) {
        setUserName(result?.responseData?.managementDetails?.name);
      } else {
        setUserName(result?.responseData?.name);
      }
    } else {
      setUserName('');
    }
    if (result?.responseData?.profileImage
      && !!result?.responseData?.profileImage?.key) {
      setImageData((result?.responseData?.profileImage
          && !!result?.responseData?.profileImage?.key)
        ? result?.responseData?.profileImage?.key : '');
    }
  };

  const getDealerDetails = async () => {
    setOrganisationName(user?.cpName);
    const result = await dealerApi.getDealerProfile();
    if (result?.statusCode === ResponseCodes.OK) {
      if (result?.responseData?.managementDetails?.name) {
        setUserName(result?.responseData?.managementDetails?.name);
      } else {
        setUserName(result?.responseData?.name);
      }
    } else {
      setUserName('');
    }
    if (result?.responseData?.profileImage
      && !!result?.responseData?.profileImage?.key) {
      setImageData((result?.responseData?.profileImage
          && !!result?.responseData?.profileImage?.key)
        ? result?.responseData?.profileImage?.key : '');
    }
  };

  const getNameAndRoles = () => {
    setUserName(user?.name);
    setOrganisationName(user?.organization);
  };

  const getTechnicianDetails = async () => {
    setUserName(user?.name);
    const result = await technicianApi.getTechnicianById(user?.technicianId);
    if (result?.statusCode === ResponseCodes.OK) {
      setOrganisationName(result?.responseData?.channelPartner?.name);
    } else {
      setOrganisationName('');
    }
    if (result?.responseData?.profileImage
      && !!result?.responseData?.profileImage?.key) {
      setImageData((result?.responseData?.profileImage
          && !!result?.responseData?.profileImage?.key)
        ? result?.responseData?.profileImage?.key : '');
    }
  };

  const route = () => {
    if (user?.scope === Roles.WEB_ADMIN) {
      return '/dashboard/about-us';
    } if (user?.scope === Roles.TECHNICIAN) {
      return '/dashboard';
    }
    return '/dashboard/my-profile';
  };

  useEffect(() => {
    if (user?.scope === Roles.CHANNEL_PARTNER) {
      getChannelPartnerDetails();
    } else if (user?.scope === Roles.WEB_ADMIN) {
      getNameAndRoles();
    } else if (user?.scope === Roles.TECHNICIAN) {
      getTechnicianDetails();
    } else if (user?.scope === Roles.DEALER) {
      getDealerDetails();
    }
  }, [user?.scope, user?.technicianId]);

  return (
    <>
      <Box
        ref={anchorRef}
        sx={{
          display: 'flex',
          cursor: 'pointer',
          ml: '2.5px'
        }}
        {...other}
      >
        {imageUrl ? (
          <Avatar
            onClick={() => {
              handleOpen();
              if (!pinned) { onPin(); }
            }}
            src={imageUrl || '/static/default-profile-pic.png'}
            variant="circular"
            sx={{
              backgroundColor: 'transparent',
              height: 40,
              width: 40 }}
          />
        ) : (
          <Box
            onClick={() => {
              handleOpen();
              if (!pinned) { onPin(); }
            }}
            sx={{
              display: 'inline-block',
              height: '35px',
              width: '35px',
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: '18px',
              alignContent: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              onClick={() => {
                handleOpen();
                if (!pinned) { onPin(); }
              }}
              sx={{
                textAlign: 'center',
                p: '6px',
                fontSize: '16px',
                fontWeight: '700',
              }}
            >
              {`${(userName.split(' ')[0].slice(0, 1))?.toUpperCase()}`}
              {userName.split(' ').length > 1 && (
                (userName.split(' ')[userName.split(' ').length - 1]?.slice(0, 1))?.toUpperCase()
              )}
            </Typography>
          </Box>
        )}

        <Box
          onClick={() => {
            handleOpen();
            if (!pinned) { onPin(); }
          }}
          sx={{
            alignItems: 'center',
            display: {
              md: showName ? 'flex' : 'none',
              xs: 'none'
            },
            flex: 1,
            ml: 1,
            minWidth: 120
          }}
        >
          <div>
            {/* <Typography
              sx={{
                color: lightNeutral[500]
              }}
              variant="caption"
            >
              Operation
            </Typography> */}
            <Typography
              sx={{ color: 'primary.contrastText', width: '130px' }}
              variant="subtitle2"
            >
              {user?.scope === Roles.WEB_ADMIN
                ? user?.name : userName}
            </Typography>
          </div>
        </Box>
        <Box
          onClick={onPin}
          sx={{
            alignItems: 'center',
            display: {
              md: showName ? 'flex' : 'none',
              xs: 'none'
            },
            flex: 1,
            ml: 1,
          }}
        >
          {pinned ? (
            <ChevronLeftIcon
              sx={{
                color: 'primary.contrastText',
                ml: 1
              }}
            />
          ) : (
            <PushPinIcon sx={{
              color: 'primary.contrastText',
            }}
            />
          )}

        </Box>
      </Box>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom'
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            width: 260,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <List>
          { tabView && (
            <>
              <Typography
                color="primary.main"
                sx={{
                  px: 2,
                  pt: 1,
                  pb: 1,
                  fontWeight: 'bold',
                }}
                variant="subtitle2"
              >
                {user?.scope === Roles.WEB_ADMIN
                  ? user?.name : userName}
              </Typography>
              <Divider />
            </>
          )}
          <ListItem
            button
            component={RouterLink}
            divider
            onClick={handleClose}
            to="/dashboard/account"
          >
            <ListItemIcon>
              <UserIcon />
            </ListItemIcon>
            <ListItemText primary="Account" />
          </ListItem>
          <ListItem
            button
            onClick={handleLogout}
          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Log out" />
          </ListItem>
        </List>
      </Popover>
    </>
  );
};

AccountPopover.propTypes = {
  // @ts-ignore
  darkMode: PropTypes.bool.isRequired,
  onSwitchTheme: PropTypes.func.isRequired,
  showName: PropTypes.bool.isRequired,
  onPin: PropTypes.func.isRequired,
  pinned: PropTypes.bool.isRequired
};
